import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { Crown, Check, X, Clock, Calendar, CreditCard, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscription = ({ user, onLogout }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('14-day trial started!');
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24 md:pb-8">
        <Navigation user={user} onLogout={onLogout} currentPage="subscription" />
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Loading subscription...</p>
        </div>
      </div>
    );
  }

  const isPremium = subscriptionStatus?.plan === 'premium' && 
                    (subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trial');

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      <Navigation user={user} onLogout={onLogout} currentPage="subscription" />
      
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Subscription & Billing</h1>

        {/* Current Plan Card */}
        <div className="card mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-xl sm:text-2xl font-semibold">Current Plan</h2>
            <div className={`px-3 sm:px-4 py-2 rounded-full flex items-center gap-2 w-fit ${
              isPremium ? 'bg-gradient-to-r from-[#A2DAD3] to-[#8fc9c0] text-white' : 'bg-gray-200'
            }`}>
              {isPremium && <Crown size={18} />}
              <span className="font-semibold text-sm sm:text-base">
                {subscriptionStatus?.admin_granted_premium ? 'Premium (Admin)' :
                 subscriptionStatus?.status === 'trial' ? 'Premium (Trial)' :
                 isPremium ? 'Premium' : 'Free'}
              </span>
            </div>
          </div>

          {subscriptionStatus?.status === 'trial' && subscriptionStatus?.trial_end_date && (
            <div className="mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 flex items-start gap-3">
              <Clock size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900">Trial Active</p>
                <p className="text-sm text-yellow-700">
                  Your trial ends on {new Date(subscriptionStatus.trial_end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {subscriptionStatus?.status === 'expired' && (
            <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Trial Expired</p>
                <p className="text-sm text-red-700">
                  Your trial has ended. Upgrade to continue using premium features.
                </p>
              </div>
            </div>
          )}

          {!isPremium && subscriptionStatus?.status !== 'trial' && (
            <div className="mb-4">
              <p className="text-gray-600">You're currently on the free plan.</p>
            </div>
          )}
        </div>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Free Plan */}
          <div className="card border-2" style={{ borderColor: '#e5e5e5' }}>
            <h3 className="text-2xl font-bold mb-2">Free Plan</h3>
            <p className="text-gray-600 mb-4">Perfect to get started</p>
            <div className="text-3xl font-bold mb-6">$0<span className="text-lg text-gray-500">/forever</span></div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Create 1 patient</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Track symptoms</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Record blood pressure & sugar</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Log medications</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Use basic reminders</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Export reports</span>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Not included:</p>
                <div className="flex items-start gap-2">
                  <X size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">Additional patients</span>
                </div>
                <div className="flex items-start gap-2">
                  <X size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">Log doctors appointments</span>
                </div>
                <div className="flex items-start gap-2">
                  <X size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">Log food restrictions</span>
                </div>
                <div className="flex items-start gap-2">
                  <X size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">Advanced analytics</span>
                </div>
              </div>
            </div>

            {!isPremium && (
              <button 
                disabled 
                className="w-full btn opacity-50 cursor-not-allowed"
                style={{ backgroundColor: '#e5e5e5', color: '#666' }}
              >
                Current Plan
              </button>
            )}
          </div>

          {/* Premium Plan */}
          <div className="card border-2 relative" style={{ borderColor: '#A2DAD3' }}>
            <div className="absolute top-0 right-0 px-4 py-1 rounded-bl-lg text-white text-sm font-semibold"
                 style={{ backgroundColor: '#A2DAD3' }}>
              Most Popular
            </div>
            
            <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Crown size={24} style={{ color: '#A2DAD3' }} />
              Premium Plan
            </h3>
            <p className="text-gray-600 mb-4">Unlock all features</p>
            
            <div className="flex items-baseline gap-3 mb-2">
              <div className="text-3xl font-bold">$5<span className="text-lg text-gray-500">/month</span></div>
              <span className="text-gray-500">or</span>
              <div className="text-3xl font-bold">$39<span className="text-lg text-gray-500">/year</span></div>
            </div>
            <p className="text-sm text-green-600 font-medium mb-6">Save $21 with annual plan!</p>

            <div className="space-y-3 mb-6">
              <p className="font-semibold text-sm" style={{ color: '#A2DAD3' }}>Everything in Free, plus:</p>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Unlimited patients</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Log doctors appointments</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Log food restrictions</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Advanced analytics & trends</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Long-term trend reports</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Advanced alerts</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">Priority support</span>
              </div>
            </div>

            {isPremium ? (
              <button 
                disabled 
                className="w-full btn flex items-center justify-center gap-2"
                style={{ backgroundColor: '#A2DAD3', color: 'white' }}
              >
                <Crown size={20} />
                Current Plan
              </button>
            ) : subscriptionStatus?.status === 'trial' ? (
              <button 
                disabled 
                className="w-full btn opacity-50"
                style={{ backgroundColor: '#A2DAD3', color: 'white' }}
              >
                Trial Active
              </button>
            ) : (
              <button 
                onClick={handleStartTrial}
                className="w-full btn flex items-center justify-center gap-2"
                style={{ backgroundColor: '#A2DAD3', color: 'white' }}
              >
                <Clock size={20} />
                Start 14-Day Free Trial
              </button>
            )}
          </div>
        </div>

        {/* Payment Coming Soon Section */}
        <div className="card bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="flex items-start gap-4">
            <CreditCard size={32} className="text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-semibold mb-2">Payment Integration Coming Soon</h3>
              <p className="text-gray-600 mb-4">
                We're working on integrating secure payment processing to make upgrading seamless. 
                In the meantime, you can start your 14-day free trial to experience all premium features!
              </p>
              <div className="space-y-2 text-sm text-gray-600">
                <p>✓ Secure payment processing</p>
                <p>✓ Multiple payment methods</p>
                <p>✓ Easy subscription management</p>
                <p>✓ Cancel anytime</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
